import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { LoHiMappableDriver } from 'src/app/map/map.service';
import { LoadApprovalDialogComponent } from 'src/app/shared/load-approval-dialog/load-approval-dialog.component';
import { ActiveDriver, Card, ICONS } from 'src/app/types';
import {
  DriverDataBrief,
  DriverInProgress,
  DriverOnHitch,
  DriverOnShift,
  LohiDashboardService,
  LoHiSandiLoadNeedingAdminApproval,
} from '../lohi-dashboard.service';

@Component({
  selector: 'lohi-app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [CurrencyPipe, DatePipe],
})
export class DashboardComponent implements OnInit {
  public drivers$: Observable<LoHiMappableDriver[]>;
  public highlightedDriver$$ = new BehaviorSubject<ActiveDriver>(null);
  public cards: Card[] = [];
  public currentTab: 'in-progress' | 'on-shift' | 'on-hitch' = 'in-progress';

  public driversInProgress: DriverInProgress[] = [];
  public driversOnShift: DriverOnShift[] = [];
  public driversOnHitch: DriverOnHitch[] = [];

  public allDrivers: any[] = [];
  public selectedDriver: DriverInProgress | DriverOnShift | DriverOnHitch | null = null;
  public loadsNeedingAcceptance: LoHiSandiLoadNeedingAdminApproval[] = [];
  public adminAcceptanceAvailableDrivers: DriverDataBrief[] = [];

  constructor(
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private dashboardService: LohiDashboardService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    this.initializeCards();
    this.loadDrivers();
    this.loadLoadsNeedingAcceptance();
  }

  private loadLoadsNeedingAcceptance(): void {
    this.dashboardService.getLoadsNeedingAdminAcceptance$().subscribe({
      next: ({ loads, availableDrivers }) => {
        this.loadsNeedingAcceptance = loads ?? [];
        this.adminAcceptanceAvailableDrivers = availableDrivers ?? [];
      },
      error: (err) => console.error('Error loading loads needing acceptance:', err),
    });
  }

  public openLoadApprovalDialog(load: LoHiSandiLoadNeedingAdminApproval): void {
    const dialogRef = this.dialog.open(LoadApprovalDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      data: { load, availableDrivers: this.adminAcceptanceAvailableDrivers },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadLoadsNeedingAcceptance();
      }
    });
  }

  private loadDrivers(): void {
    forkJoin({
      inProgress$: this.dashboardService.getDriverInProgress$(),
      onShift$: this.dashboardService.getDriverOnShift$(),
      onHitch$: this.dashboardService.getDriverOnHitch$(),
    }).subscribe({
      next: (response) => {
        this.allDrivers = [
          ...(response.inProgress$?.map((driver) => ({ ...driver, status: 'in-progress' })) || []),
          ...(response.onShift$?.map((driver) => this.formatDriverData(driver, 'on-shift')) || []),
          ...(response.onHitch$?.map((driver) => ({ ...driver, status: 'on-hitch' })) || []),
        ];

        const firstInProgressDriver = this.allDrivers.find((driver) => driver.status === 'in-progress');
        if (firstInProgressDriver) {
          this.selectDriver(firstInProgressDriver);
        }
      },
      error: (err) => {
        console.error('Error loading driver data:', err);
      },
    });
  }

  private formatDriverData(driver: any, status: string): any {
    const formattedShiftStart = this.datePipe.transform(driver.shiftStartAt, 'M/d/yy h:mm a', 'UTC');

    const hosHours = Math.floor(driver.hosRemainingMinutes / 60);
    const hosMinutes = Math.round(driver.hosRemainingMinutes % 60);

    const lastLoadHours = Math.floor(driver.minutesSinceLastCompletedLoad / 60);
    const lastLoadMinutes = Math.round(driver.minutesSinceLastCompletedLoad % 60);

    return {
      ...driver,
      status,
      shiftStartAt: formattedShiftStart,
      hosRemaining: `${hosHours}h ${hosMinutes}m`,
      durationSinceLastLoad: `${lastLoadHours}h ${lastLoadMinutes}m`,
    };
  }

  private initializeCards(): void {
    this.cards = [
      { title: 'On-time performance', ...this.getDefaultCardProps() },
      { title: 'Total revenue', ...this.getDefaultCardProps() },
      { title: 'Missed Revenue', ...this.getDefaultCardProps() },
      { title: 'Average turn rate', ...this.getDefaultCardProps() },
    ];

    this.dashboardService.getOnTimePerformance$().subscribe({
      next: (response) => {
        this.updateCard(0, {
          value: response.onTimePerformance ? `${response.onTimePerformance}%` : '-',
          ...this.getIndicatorSettings('onTimePerformance', response.rank.arrowDirection === 'up', response.rank),
        });
      },
      error: () => {
        this.updateCardError(0);
      },
    });

    this.dashboardService.getRevenue$().subscribe({
      next: (response) => {
        const formattedRevenue = this.currencyPipe.transform(response.totalRevenueCent / 100, 'USD', 'symbol', '1.2-2');
        this.updateCard(1, {
          value: formattedRevenue || '-',
          ...this.getIndicatorSettings('revenue', response.rank.arrowDirection === 'up', response.rank),
        });
      },
      error: () => {
        this.updateCardError(1);
      },
    });

    this.dashboardService.getLostRevenueByDriver$().subscribe({
      next: (response) => {
        const sumAllDriverOverallMissedRevenue = response.reduce((acc, driver) => acc + driver.overallMissedRevenue, 0);
        const formattedMissedRevenue = this.currencyPipe.transform(
          sumAllDriverOverallMissedRevenue / 100,
          'USD',
          'symbol',
          '1.2-2',
        );
        this.updateCard(2, {
          value: formattedMissedRevenue || '-',
          ...this.getIndicatorSettings('lostRevenue', false),
        });
      },
      error: () => {
        this.updateCardError(2);
      },
    });

    this.dashboardService.getAverageTurnRate$().subscribe({
      next: (response) => {
        this.updateCard(3, {
          value: response.average_turn_rate?.toFixed(1) || '-',
          ...this.getIndicatorSettings('averageTurnRate', response.is_top_25_percent),
        });
      },
      error: () => {
        this.updateCardError(3);
      },
    });
  }

  private getDefaultCardProps(): Omit<Card, 'title'> {
    return {
      value: '-',
      subtext: 'Past 7 days',
      infoIcon: ICONS.information,
      indicatorIcon: '',
      indicatorText: '',
      textColor: '',
      indicatorClass: '',
    };
  }

  private getIndicatorSettings(
    metric: 'onTimePerformance' | 'revenue' | 'lostRevenue' | 'missedRevenue' | 'averageTurnRate',
    isTop25: boolean,
    rank?: { rankText: string; color: string; arrowDirection: string },
  ): Partial<Card> {
    switch (metric) {
      case 'onTimePerformance':
        return {
          indicatorIcon: isTop25 ? ICONS.upArrowGreen : ICONS.bottomArrowRed,
          indicatorText: rank.rankText,
          textColor: isTop25 ? 'text-green-600' : 'text-red-500',
          indicatorClass: isTop25 ? 'text-lightGreen' : 'text-red-500',
        };
      case 'revenue':
        return {
          indicatorIcon: isTop25 ? ICONS.upArrowGreen : ICONS.bottomArrowRed,
          indicatorText: rank.rankText,
          textColor: isTop25 ? 'text-green-600' : 'text-red-500',
          indicatorClass: isTop25 ? 'text-lightGreen' : 'text-red-500',
        };
      case 'lostRevenue':
        return {
          indicatorIcon: '',
          indicatorText: '',
          textColor: '',
          indicatorClass: '',
        };
      case 'missedRevenue':
        return {
          indicatorIcon: isTop25 ? ICONS.upRedArrow : ICONS.upArrowGreen,
          indicatorText: isTop25 ? 'Top 25% of the region' : '',
          textColor: isTop25 ? 'text-red-500' : '',
          indicatorClass: isTop25 ? 'text-red-500' : '',
        };
      case 'averageTurnRate':
        return {
          indicatorIcon: isTop25 ? ICONS.upArrowGreen : '',
          indicatorText: '',
          textColor: '',
          indicatorClass: '',
        };
      default:
        return {};
    }
  }

  private updateCard(index: number, data: Partial<Card>): void {
    this.cards[index] = { ...this.cards[index], ...data };
  }

  private updateCardError(index: number): void {
    this.updateCard(index, {
      value: '-',
      indicatorIcon: '',
      indicatorText: '',
      textColor: '',
      indicatorClass: '',
    });
  }

  public get filteredDrivers(): any[] {
    return this.allDrivers.filter((driver) => driver.status === this.currentTab);
  }

  public switchTab(index: number): void {
    const tabMapping = ['in-progress', 'on-shift', 'on-hitch'];
    this.currentTab = tabMapping[index] as 'in-progress' | 'on-shift' | 'on-hitch';
  }

  public identifyDriver(index: number, driver: DriverInProgress): any {
    return driver.driverId;
  }

  public trackByLoadId(index: number, load: LoHiSandiLoadNeedingAdminApproval): string {
    return load.id;
  }

  public selectDriver(driver: any): void {
    this.selectedDriver = driver;
  }
}
